import { appNavigate } from './navigationRef';

export const MAINTENANCE_CONFIG = {
  ENABLED: process.env.REACT_APP_MAINTENANCE_MODE === 'true',
  MESSAGE: (
    process.env.REACT_APP_MAINTENANCE_MESSAGE?.trim() ||
    "We're currently performing scheduled maintenance. Please check back soon."
  ).replace(/\\n/g, '\n'),
  ALLOWED_LIST: (process.env.REACT_APP_ALLOWED_LIST || '')
    .split(',')
    .map((e) => e.trim().toLowerCase())
    .filter(Boolean),
};

export const isAllowedBetaEmail = (email: string): boolean =>
  MAINTENANCE_CONFIG.ALLOWED_LIST.includes(email.trim().toLowerCase());

// API Configuration
export const API_CONFIG = {
  BASE_URL: process.env.REACT_APP_API_URL || 'http://localhost:8000/api/',
  INTERVIEW_API_URL: process.env.REACT_APP_INTERVIEW_API_URL || 'http://localhost:8000',
  ASR_WEBSOCKET_URL: process.env.REACT_APP_ASR_WS_URL || 'ws://localhost:8000/ws/asr',
  WEBSOCKET_URL: process.env.REACT_APP_WS_URL || 'ws://localhost:8000/ws/listening',
  AUTH_BASE_URL: process.env.REACT_APP_AUTH_API_URL || 'http://localhost:8002',
  
  TIMEOUT: 15000,
  
  // BASE_URL: process.env.REACT_APP_API_URL || 'https://letuscrackit-production.up.railway.app/api/',
  // INTERVIEW_API_URL: process.env.REACT_APP_INTERVIEW_API_URL || 'https://letuscrackit-production.up.railway.app',
  // ASR_WEBSOCKET_URL: process.env.REACT_APP_ASR_WS_URL || 'wss://letuscrackit-production.up.railway.app/ws/asr',
  // WEBSOCKET_URL: process.env.REACT_APP_WS_URL || 'wss://letuscrackit-production.up.railway.app/ws/listening',
  // AUTH_BASE_URL: process.env.REACT_APP_AUTH_API_URL || 'https://authserviceluci-production.up.railway.app',
  // PISTON_BASE_URL: process.env.REACT_APP_PISTON_BASE_URL || 'http://localhost:8088/api/v2',
  
  // BASE_URL: process.env.REACT_APP_API_URL || 'https://api.letuscrackit.ai/api/',
  // INTERVIEW_API_URL: process.env.REACT_APP_INTERVIEW_API_URL || 'https://api.letuscrackit.ai',
  // VIDEO_API_URL: process.env.REACT_APP_VIDEO_API_URL || 'https://api.letuscrackit.ai',
  // WEBSOCKET_URL: process.env.REACT_APP_WS_URL || 'wss://api.letuscrackit.ai/ws/listening',
  // ASR_WEBSOCKET_URL: process.env.REACT_APP_ASR_WS_URL || 'wss://api.letuscrackit.ai/ws/asr',
  // AUTH_BASE_URL: process.env.REACT_APP_AUTH_API_URL || 'https://api.letuscrackit.ai',
  
  
  //INTERVIEW_API_URL: process.env.REACT_APP_INTERVIEW_API_URL || 'https://3.214.1.90',
  //WEBSOCKET_URL: process.env.REACT_APP_WS_URL || 'ws://3.214.1.90:8000/ws/listening',
  //ASR_WEBSOCKET_URL: process.env.REACT_APP_ASR_WS_URL || 'ws://3.214.1.90:8000/ws/asr',
  
};

// Interview Configuration (fallback when API omits max_questions; aligns with backend default)
export const INTERVIEW_CONFIG = {
  MAX_QUESTIONS: 10,
  SILENCE_THRESHOLD: 2000, // 2 seconds
  AUDIO_CHUNK_SIZE: 250, // milliseconds
  DEFAULT_INTERVIEW_TYPE: 'general',
};

// Audio Configuration
export const AUDIO_CONFIG = {
  SAMPLE_RATE: 16000,
  CHANNEL_COUNT: 1,
  ECHO_CANCELLATION: true,
  NOISE_SUPPRESSION: true,
  AUTO_GAIN_CONTROL: true,
  SILENCE_DB_THRESHOLD: -7.0,
  MIN_DB: -60,
  MAX_DB: 0,
  /** OpenAI Realtime transcription input (classic interview) */
  OPENAI_STT_SAMPLE_RATE: 24000,
};

// UI Configuration
export const UI_CONFIG = {
  WAVEFORM_BARS: 50,
  ANIMATION_DURATION: 100, // milliseconds
  LOADING_DELAY: 2000, // milliseconds
};

// Deepgram Voice Agent plugin (practice mock interviews)
export const VOICE_AGENT_CONFIG = {
  MIC_SAMPLE_RATE: 16000,
  AGENT_AUDIO_SAMPLE_RATE: 48000,
  SILENCE_FRAME_MS: 20,
};

/** Skip GET /voice-agent/status when build sets this (dev convenience). */
export const VOICE_AGENT_BUILD_ENABLED =
  process.env.REACT_APP_VOICE_AGENT_ENABLED === 'true';

/** When true, onboarding interviews use Andrea (voice agent) instead of Luci. */
export const ONBOARDING_FAST_INTERVIEW_ENABLED =
  process.env.REACT_APP_ONBRD_INT_FAST === 'true';

// Local Storage Keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'authToken',
  INTERVIEW_FEEDBACK: 'interviewFeedback',
  USER_PREFERENCES: 'userPreferences',
  SESSION_DATA: 'sessionData',
};

// Authentication Utilities
export const getAuthenticatedWebSocketUrl = (baseUrl: string): string => {
  // Return clean URL without token parameters - authentication now handled via headers
  return baseUrl;
};

export const getAuthenticatedAudioUrl = (audioUrl: string): string => {
  // Handle relative URLs by making them absolute
  let fullUrl = audioUrl;
  if (audioUrl.startsWith('/api/')) {
    fullUrl = `${API_CONFIG.INTERVIEW_API_URL}${audioUrl}`;
  }
  
  // Return clean URL without token parameters - authentication now handled via headers
  return fullUrl;
};

// Helper function to get auth headers for requests
export const getAuthHeaders = (): HeadersInit => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    throw new Error('No authentication token available');
  }
  
  return {
    'Authorization': `Bearer ${token}`,
  };
};

// CSRF Token Utilities
export const CSRF_UTILS = {
  // Get CSRF token from cookies
  getCsrfToken: (): string | null => {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrf_token') {
        return decodeURIComponent(value);
      }
    }
    return null;
  },

  // Check if CSRF token exists
  hasCsrfToken: (): boolean => {
    return CSRF_UTILS.getCsrfToken() !== null;
  },

  // Get CSRF headers for manual requests
  getCsrfHeaders: (): HeadersInit => {
    const csrfToken = CSRF_UTILS.getCsrfToken();
    return csrfToken ? { 'X-CSRF-Token': csrfToken } : {};
  },

  // Debug CSRF token (for development)
  debugCsrfToken: (): void => {
    const token = CSRF_UTILS.getCsrfToken();
    console.log('🔒 CSRF Token Debug:', {
      exists: !!token,
      value: token ? `${token.substring(0, 8)}...` : null,
      fullCookie: document.cookie.split(';').find(c => c.trim().startsWith('csrf_token='))
    });
  }
};

// Helper function to create authenticated WebSocket with browser-compatible authentication
// and automatic reconnection on auth failure
export const createAuthenticatedWebSocket = async (
  url: string, 
  options?: {
    onAuthFailure?: () => void;
    maxRetries?: number;
  }
): Promise<WebSocket> => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    throw new Error('No authentication token available for WebSocket connection');
  }
  
  console.log('🔌 Creating authenticated WebSocket with hybrid authentication (browser compatible)');
  
  // Since browsers don't support custom headers in WebSocket connections,
  // we use a hybrid approach: subprotocols first, then query parameters as fallback
  try {
    // Method 1: Try WebSocket subprotocol authentication first (cleaner)
    const authProtocol = `Bearer.${token}`;
    
    console.log('🔌 Attempting WebSocket connection with subprotocol authentication');
    const websocket = new WebSocket(url, [authProtocol]);
    
    let authTimeout: NodeJS.Timeout;
    let connectionResolved = false;
    let fallbackInitiated = false;
    
    return new Promise((resolve, reject) => {
      const createFallback = (reason: string): void => {
        if (fallbackInitiated || connectionResolved) return;
        fallbackInitiated = true;
        
        console.log(`🔄 Creating fallback WebSocket (reason: ${reason})`);
        const separator = url.includes('?') ? '&' : '?';
        const fallbackUrl = `${url}${separator}token=${encodeURIComponent(token)}`;
        
        const fallbackWebsocket = new WebSocket(fallbackUrl);
        
        fallbackWebsocket.addEventListener('open', () => {
          console.log('✅ WebSocket connected with query parameter authentication (fallback)');
          connectionResolved = true;
          resolve(fallbackWebsocket);
        });
        
        fallbackWebsocket.addEventListener('close', (evt) => {
          if (evt.code === 1008) {
            console.error('🔐 WebSocket authentication failed - invalid or expired token');
            if (options?.onAuthFailure) {
              options.onAuthFailure();
            } else {
              localStorage.removeItem('authToken');
              appNavigate('/login', { replace: true });
            }
          }
        });
        
        fallbackWebsocket.addEventListener('error', (error) => {
          console.error('❌ Fallback WebSocket connection failed:', error);
          if (!connectionResolved) {
            connectionResolved = true;
            reject(error);
          }
        });
      };

      authTimeout = setTimeout(() => {
        if (!connectionResolved) {
          console.log('⚠️ Subprotocol authentication timeout, trying query parameter fallback...');
          websocket.close();
          createFallback('timeout');
        }
      }, 10000);
      
      websocket.addEventListener('open', () => {
        console.log('✅ WebSocket connected with subprotocol authentication');
        connectionResolved = true;
        clearTimeout(authTimeout);
        resolve(websocket);
      });
      
      websocket.addEventListener('close', (event) => {
        if (!connectionResolved) {
          clearTimeout(authTimeout);
          createFallback('subprotocol closed before open');
        } else if (event.code === 1008) {
          console.error('🔐 WebSocket authentication failed - invalid or expired token');
          if (options?.onAuthFailure) {
            options.onAuthFailure();
          } else {
            localStorage.removeItem('authToken');
            appNavigate('/login', { replace: true });
          }
        }
      });
      
      websocket.addEventListener('error', (error) => {
        console.error('❌ Primary WebSocket connection failed:', error);
        if (!connectionResolved) {
          // Don't reject immediately, let the timeout/close handle fallback
          console.log('🔄 Primary connection failed, waiting for timeout to try fallback...');
        }
      });
    });
    
  } catch (error) {
    console.error('❌ WebSocket connection setup failed:', error);
    throw error;
  }
};

// Helper function to handle WebSocket authentication failures with token refresh and reconnection
export const handleWebSocketAuthFailure = async (
  url: string,
  onReconnected: (ws: WebSocket) => void,
  onFinalFailure: () => void
): Promise<void> => {
  console.log('🔄 [WebSocket] Handling authentication failure - attempting token refresh...');
  
  try {
    // Try to refresh the token
    const authClient = (await import('../api/authClient')).default;
    const refreshResponse = await authClient.post('/auth/refresh');
    const newToken = refreshResponse.data.access_token;
    
    // Update stored token
    localStorage.setItem('authToken', newToken);
    console.log('✅ [WebSocket] Token refreshed successfully, reconnecting...');
    
    // Create new WebSocket with fresh token
    const newWebSocket = await createAuthenticatedWebSocket(url, {
      onAuthFailure: onFinalFailure
    });
    
    console.log('✅ [WebSocket] Reconnected successfully with refreshed token');
    onReconnected(newWebSocket);
    
  } catch (error) {
    console.error('❌ [WebSocket] Token refresh failed:', error);
    onFinalFailure();
  }
};

// Helper function to fetch authenticated audio as blob URL
export const fetchAuthenticatedAudio = async (audioUrl: string): Promise<string> => {
  const token = localStorage.getItem('authToken');
  if (!token) {
    throw new Error('No authentication token available for audio URL');
  }
  
  // Handle different URL formats
  let fullUrl = audioUrl;
  const isAbsoluteUrl = audioUrl.startsWith('http://') || audioUrl.startsWith('https://');
  const isS3Url = audioUrl.includes('.s3.') || audioUrl.includes('s3.amazonaws.com');
  
  // If it's an S3 URL, it's likely publicly accessible - return as-is
  if (isS3Url) {
    console.log('🔊 S3 audio URL detected, using directly:', audioUrl);
    return audioUrl;
  }
  
  // Handle relative URLs by making them absolute
  if (!isAbsoluteUrl) {
    // Any relative URL (starting with / or not) - prefix with INTERVIEW_API_URL
    if (audioUrl.startsWith('/')) {
      fullUrl = `${API_CONFIG.INTERVIEW_API_URL}${audioUrl}`;
    } else {
      fullUrl = `${API_CONFIG.INTERVIEW_API_URL}/${audioUrl}`;
    }
    console.log('🔊 Converted relative audio URL to:', fullUrl);
  }
  
  try {
    console.log('🔊 Fetching audio with auth headers:', fullUrl);
    const response = await fetch(fullUrl, {
      headers: {
        'Authorization': `Bearer ${token}`,
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch audio: ${response.status}`);
    }
    
    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error('Error fetching authenticated audio:', error);
    throw error;
  }
};

// Error Messages
export const ERROR_MESSAGES = {
  NETWORK_ERROR: 'Network error - please check if the backend is running',
  MIC_ACCESS_DENIED: 'Please allow microphone access to continue the interview',
  MIC_NOT_FOUND: 'No microphone found. Please connect a microphone',
  MIC_IN_USE: 'Microphone is being used by another application',
  AUDIO_BLOCKED: 'Audio blocked by browser - continuing interview without sound',
  CONNECTION_LOST: 'Connection lost - please refresh the page',
  INTERVIEW_START_FAILED: 'Failed to start interview automatically',
};

// Success Messages
export const SUCCESS_MESSAGES = {
  INTERVIEW_STARTED: 'Interview started successfully',
  INTERVIEW_COMPLETED: 'Interview completed! Thank you for your time.',
  AUDIO_READY: 'Audio system ready',
  CONNECTION_ESTABLISHED: 'Connected to interview system',
};

// Interview Status
export const INTERVIEW_STATUS = {
  INITIALIZING: 'Initializing interview...',
  READY: 'Ready to start interview...',
  CONNECTING: 'Connecting to AI interviewer...',
  ACTIVE: 'Interview in progress',
  LISTENING: 'Listening... speak now!',
  PROCESSING: 'Processing your response...',
  AI_SPEAKING: 'AI is speaking...',
  PAUSED: 'Interview paused',
  COMPLETED: 'Interview completed',
  ERROR: 'Error occurred',
};
